import type {
  BattleContestDetailVo,
  BattleContestProblemDetailVo,
  BattleContestProblemVo,
  BattleContestRecordItemVo,
  BattleContestRecordVo,
  BattleContestReportVo,
  BattleContestWrongVo,
  OvertakeBattleContestVo,
  SubmitBattleContestVo,
  UnitGoodsVo,
  UnitProductVo,
  UserBattleContestProblemDetailVo,
  YinheBadgeShowVo,
} from '@core/types/auto/biz';
import type { SubmitProblemRequestVO } from '@core/types/problem';
import { httpService } from '../http';

const BASE = '/api/htoj-biz-gateway/gwpub/battle/v2';

/**
 * 开始答题，返回题目列表 + 用时
 * @param exerciseId 指定场次；当前场次与补做场次统一传入
 */
export function getStartBattleContest(exerciseId: number) {
  return httpService.get<BattleContestProblemVo>(
    `${BASE}/start-battle-contest`,
    { query: { exerciseId } },
  );
}

/**
 * 获取单题详情
 */
export function getBattleContestProblem(problemId: number, exerciseId: number) {
  return httpService.get<BattleContestProblemDetailVo>(
    `${BASE}/get-battle-contest-problem`,
    { query: { pid: problemId, exerciseId } },
  );
}

/**
 * 提交评测/选择题答案（biz 网关版）
 * 选择题：传 answers + language("choice"/"objective")
 * 编程题：传 code + language + ioMode
 */
export function submitProblemJudge(
  data: SubmitProblemRequestVO,
  exerciseId: number,
) {
  return httpService.post<unknown>(
    '/api/htoj-biz-gateway/gwpub/judge/submit-problem-judge',
    { query: { exerciseId }, body: data },
  );
}

/**
 * 检查是否可提交，返回 1=已评测，0=未评测
 */
export function getSubmitBattleContestCheck(pid: number, exerciseId: number) {
  return httpService.get<number>(`${BASE}/submit-battle-contest-check`, {
    query: { pid, exerciseId },
  });
}

/**
 * 交卷，返回累计参赛奖励、完赛奖励、抽奖次数等
 */
export function submitBattleContest(exerciseId: number) {
  return httpService.post<SubmitBattleContestVo>(
    `${BASE}/submit-battle-contest`,
    { query: { exerciseId } },
  );
}

/**
 * 获取赛事详情
 */
export function getBattleContestDetail() {
  return httpService.get<BattleContestDetailVo>(
    `${BASE}/get-battle-contest-detail`,
  );
}

/**
 * 获取参赛战绩统计（已完赛场数、谷子数、最高连续周数）
 */
export function getBattleContestCount() {
  return httpService.get<BattleContestRecordVo>(`${BASE}/battle-contest-count`);
}

/**
 * 超越其他用户
 */
export function getBattleContestOvertake(exerciseId: number) {
  return httpService.get<OvertakeBattleContestVo>(
    `${BASE}/battle-contest-overtake`,
    { query: { exerciseId } },
  );
}

/**
 * 获取用户参赛记录
 */
export function getBattleContestRecord(unitProductId: number) {
  return httpService.get<BattleContestRecordItemVo[]>(
    `${BASE}/battle-contest-record`,
    { query: { unitProductId } },
  );
}

/**
 * 获取用户错题记录（按 exercise 分组，首项含完整数据）
 */
export function getBattleContestWrong(unitProductId: number) {
  return httpService.get<BattleContestWrongVo[]>(
    `${BASE}/battle-contest-wrong`,
    { query: { unitProductId } },
  );
}

/**
 * 获取 UnitProduct 列表（供下拉选择）
 */
export function getBattleUnitProduct() {
  return httpService.get<UnitProductVo[]>(`${BASE}/battle-unit-product`);
}

/**
 * 按 exerciseId 获取单场错题详情（懒加载展开用）
 */
export function getBattleExerciseWrong(exerciseId: number) {
  return httpService.get<UserBattleContestProblemDetailVo[]>(
    `${BASE}/battle-exercise-wrong`,
    { query: { exerciseId } },
  );
}

/**
 * 获取赛后报告数据
 */
export function getBattleContestReport(exerciseId?: number) {
  return httpService.get<BattleContestReportVo>(
    `${BASE}/battle-contest-report`,
    exerciseId ? { query: { exerciseId } } : {},
  );
}

/** 抽谷子 */
export function guessGoods() {
  return httpService.get<UnitGoodsVo>(`${BASE}/guess-goods`);
}

/** 银河角标查询（热门 + 新） */
export function isHotBadgeShow() {
  return httpService.get<YinheBadgeShowVo>(
    `/api/htoj-biz-gateway/api/hot-badge/show`,
  );
}

/** 标记新角标已查看 */
export function viewNewBadge() {
  return httpService.post<boolean>(`/api/htoj-biz-gateway/api/new-badge/view`);
}
